/*
PURPOSE: Centralized copy for notification system user interface

USE CASES:
- Notification page headers and empty states
- Notification item content and formatting
- Access control messaging for non-contributors
- Loading states and error messages

DOMAIN BOUNDARIES:
- Responsible for all notification-related user-facing text
- Should align with Coral's brand voice: direct, visceral, human
- Must reinforce community value and artist discovery

ASSUMPTIONS:
- Users understand notifications relate to artist activity and community discussions
- Copy should encourage engagement with the artist community
- Aligns with brand strategy of building music's commons through connection
*/

import { getGestureTheme } from 'shared/constants/gestureThemes';

/**
 * Derive a sensible return past tense from the base past tense
 * E.g., "smiled at" -> "smiled back at", "nodded to" -> "nodded back to"
 */
const deriveReturnPast = (basePast: string): string => {
  const patterns = [
    { suffix: ' at', replacement: ' back at' },
    { suffix: ' to', replacement: ' back to' },
    { suffix: ' with', replacement: ' back with' },
  ];

  for (const { suffix, replacement } of patterns) {
    if (basePast.endsWith(suffix)) {
      return basePast.slice(0, -suffix.length) + replacement;
    }
  }

  // Fallback: just append " back"
  return `${basePast} back`;
};

/**
 * Notification Copy aligned with Coral's brand voice and community philosophy
 *
 * Based on brand strategy:
 * - Direct and visceral language about community connection
 * - Emphasizes discovery through real people's taste
 * - Reinforces that notifications connect users to artists they back
 * - Uses clear, functional language for UI states
 */
export const notificationsCopy = {
  // Page header and navigation
  page: {
    title: "Notifications",
    markAllViewed: "Mark All Viewed",
    markingViewed: "Marking as viewed...",
    settings: "Notification Settings"
  },

  // Notification content and states
  list: {
    empty: {
      title: "No notifications yet",
      message: "When artists you support share updates, respond to discussions, or release new music, you'll see them here.",
      encouragement: "Your backing connects you to artists' creative journey and community conversations."
    },
    
    loading: {
      initial: "Loading your notifications...",
      more: "Loading more notifications..."
    },
    
    error: {
      load: "Failed to load notifications. Please try refreshing the page.",
      markViewed: "Failed to mark notifications as viewed. Please try again."
    },
    
    endMessage: "You've reached the end of your notifications",
    loadMore: "Load More Notifications"
  },

  // Notification content formatting
  content: {
    types: {
      new_discussion: {
        // Context-aware title for dancefloor shares vs regular discussions
        title: (context: NotificationContextData) => {
          // Check for dancefloor share
          if (context?.dancefloorTargetDisplayName) {
            return `shared music to ${context.dancefloorTargetDisplayName}'s dancefloor`;
          }
          return 'started a conversation';
        },
        icon: "💬"
      },
      response_to_contribution: {
        title: "responded to your conversation",
        icon: "↩️"
      },
      aligned_review: {
        title: "shared a review",
        icon: "⭐"
      },
      artist_activity: {
        title: "has new activity",
        icon: "🎵"
      },
      comment_mention: {
        title: "mentioned you in a conversation",
        icon: "@"
      },
      user_post_created: {
        title: "shared a personal update",
        icon: "📝"
      },
      gesture_received: {
        // Context-aware title based on gesture type and target user's theme
        title: (context: NotificationContextData) => {
          const gestureType = context?.gestureType;
          const targetTheme = context?.targetTheme;

          if (gestureType) {
            const theme = getGestureTheme(targetTheme);
            // Map 'boogie' to 'dance_with' for theme lookup (content uses 'boogie', themes use 'dance_with')
            const themeLookupType = gestureType === 'boogie' ? 'dance_with' : gestureType;
            const gestureLabel = theme.gestures[themeLookupType as keyof typeof theme.gestures];
            return `${gestureLabel?.past || 'sent you a gesture'} you`;
          }

          return 'sent you a gesture';
        },
        icon: "👋"
      },
      gesture_speak_to_created: {
        // Context-aware title showing who was spoken to, using themed gesture verb
        title: (context: NotificationContextData) => {
          const gestureType = context?.gestureType;
          const targetTheme = context?.targetTheme;
          const targetName = context?.targetDisplayName || 'someone';

          if (gestureType) {
            const theme = getGestureTheme(targetTheme);
            // Map 'boogie' to 'dance_with' for theme lookup
            const themeLookupType = gestureType === 'boogie' ? 'dance_with' : gestureType;
            const gestureLabel = theme.gestures[themeLookupType as keyof typeof theme.gestures];
            if (gestureLabel?.past) {
              return `${gestureLabel.past} ${targetName}`;
            }
          }

          return `spoke to ${targetName}`;
        },
        icon: "💬"
      },
      gesture_returned: {
        // Context-aware title using themed returnPast (e.g., "smiled back")
        // Note: We strip trailing preposition + "you" since it's redundant in notification context
        title: (context: NotificationContextData) => {
          const gestureType = context?.gestureType;
          const targetTheme = context?.targetTheme;

          if (gestureType) {
            const theme = getGestureTheme(targetTheme);
            // Map 'boogie' to 'dance_with' for theme lookup
            const themeLookupType = gestureType === 'boogie' ? 'dance_with' : gestureType;
            const gestureLabel = theme.gestures[themeLookupType as keyof typeof theme.gestures];
            // Use explicit returnPast if defined, otherwise derive from past
            const returnPast = gestureLabel?.returnPast || deriveReturnPast(gestureLabel?.past || 'gestured back');
            // Strip trailing preposition for cleaner notification copy (context makes "you" implicit)
            const cleanReturnPast = returnPast
              .replace(/ with$/, '')
              .replace(/ at$/, '')
              .replace(/ to$/, '');
            return cleanReturnPast;
          }

          return 'gestured back';
        },
        icon: "🔄"
      },
      default: {
        title: "has an update",
        icon: "📢"
      }
    },
    
    timeFormat: {
      justNow: "Just now",
      minutesAgo: "m ago",
      hoursAgo: "h ago", 
      daysAgo: "d ago"
    }
  },

  // Access control messaging for non-contributors
  accessControl: {
    title: "Notifications unlock with your backing",
    message: "Stay connected to artists you support through real-time updates on their creative journey.\n\nYour coral gives you a direct line to conversations, new releases, and community discussions across every artist and scene.\n\nBacking artists means being part of their story, not just streaming their music.",
    cta: {
      primary: "+ CREATE CORAL",
      secondary: "Learn More"
    }
  },

  // Contextual messaging for alignment status
  alignmentContext: {
    aligned_contributor: {
      description: "Someone whose taste aligns with yours",
      relevance: "This person backs similar artists to you"
    },
    backed_artist: {
      description: "An artist you support",
      relevance: "This artist is part of your coral"
    },
    community_member: {
      description: "A community member",
      relevance: "Someone engaging with music you support"
    }
  },

  // Header contextual messages
  unreadCount: {
    single: "unread notification",
    multiple: "unread notifications",
    none: "All caught up!"
  },

  // Interaction states
  actions: {
    markViewed: {
      success: "Marked as viewed",
      error: "Failed to mark as viewed"
    },
    navigate: {
      loading: "Opening...",
      error: "Unable to open content"
    }
  }
};

// Type for notification context data
type NotificationContextData = {
  gestureType?: 'speak_to' | 'smile' | 'nod' | 'boogie';
  targetDisplayName?: string;
  targetTheme?: 'default' | 'moshpit' | 'jazzclub' | 'concert';
  // Dancefloor share context
  dancefloorTargetUserId?: string;
  dancefloorTargetDisplayName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

/**
 * Helper function to get notification type display information
 * @param notificationType - The type of notification
 * @returns Display information for the notification type
 */
export const getNotificationTypeDisplay = (notificationType: string) => {
  return notificationsCopy.content.types[notificationType as keyof typeof notificationsCopy.content.types] ||
         notificationsCopy.content.types.default;
};

/**
 * Helper function to format notification timestamps
 * @param timestamp - ISO timestamp string
 * @returns Human-readable time format
 */
export const formatNotificationTime = (timestamp: string): string => {
  const now = new Date();
  const notificationTime = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - notificationTime.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return notificationsCopy.content.timeFormat.justNow;
  }
  
  if (diffInMinutes < 60) {
    return `${diffInMinutes}${notificationsCopy.content.timeFormat.minutesAgo}`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}${notificationsCopy.content.timeFormat.hoursAgo}`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}${notificationsCopy.content.timeFormat.daysAgo}`;
  }
  
  return notificationTime.toLocaleDateString();
};

/**
 * Helper function to get alignment context information
 * @param alignmentStatus - The alignment status from notification context
 * @returns Alignment context information
 */
export const getAlignmentContext = (alignmentStatus?: string) => {
  if (!alignmentStatus) return null;
  
  return notificationsCopy.alignmentContext[alignmentStatus as keyof typeof notificationsCopy.alignmentContext] || 
         notificationsCopy.alignmentContext.community_member;
};